import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, differenceInDays, parseISO } from 'date-fns';

export interface ClientStreak {
  id: string;
  client_id: string;
  current_streak: number;
  longest_streak: number;
  last_workout_date: string | null;
  total_workouts: number;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  requirement_type: string;
  requirement_value: number;
  points: number;
}

export interface ClientAchievement {
  id: string;
  client_id: string;
  achievement_id: string;
  earned_at: string;
  achievement?: Achievement;
}

export interface NewAchievement {
  achievement: Achievement;
  isNew: boolean;
}

export function useGamification(clientId: string | undefined) {
  const [streak, setStreak] = useState<ClientStreak | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [earnedAchievements, setEarnedAchievements] = useState<ClientAchievement[]>([]);
  const [newlyUnlocked, setNewlyUnlocked] = useState<NewAchievement[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      // Load streak data
      const { data: streakData } = await supabase
        .from('client_streaks')
        .select('*')
        .eq('client_id', clientId)
        .single();

      if (streakData) {
        setStreak(streakData);
      }

      // Load all achievements
      const { data: achievementsData } = await supabase
        .from('achievements')
        .select('*')
        .order('requirement_value', { ascending: true });

      if (achievementsData) {
        setAchievements(achievementsData);
      }

      // Load earned achievements
      const { data: earnedData } = await supabase
        .from('client_achievements')
        .select('*, achievement:achievements(*)')
        .eq('client_id', clientId);

      if (earnedData) {
        setEarnedAchievements(earnedData);
      }
    } catch (error) {
      console.error('Error loading gamification data:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const recordWorkout = useCallback(async () => {
    if (!clientId) return [];

    const today = format(new Date(), 'yyyy-MM-dd');
    const newUnlocked: NewAchievement[] = [];

    try {
      // Get or create streak record
      let currentStreak = streak;
      
      if (!currentStreak) {
        const { data: newStreak, error } = await supabase
          .from('client_streaks')
          .insert({
            client_id: clientId,
            current_streak: 1,
            longest_streak: 1,
            last_workout_date: today,
            total_workouts: 1
          })
          .select()
          .single();

        if (error) throw error;
        currentStreak = newStreak;
      } else {
        // Calculate new streak
        let newCurrentStreak = currentStreak.current_streak;
        const lastWorkout = currentStreak.last_workout_date;

        if (lastWorkout) {
          const daysSinceLastWorkout = differenceInDays(
            new Date(today),
            parseISO(lastWorkout)
          );

          if (daysSinceLastWorkout === 0) {
            // Already worked out today, just increment total
          } else if (daysSinceLastWorkout === 1) {
            // Consecutive day - increment streak
            newCurrentStreak += 1;
          } else {
            // Streak broken - reset to 1
            newCurrentStreak = 1;
          }
        } else {
          newCurrentStreak = 1;
        }

        const newLongestStreak = Math.max(newCurrentStreak, currentStreak.longest_streak);
        const newTotalWorkouts = currentStreak.total_workouts + 1;

        const { data: updatedStreak, error } = await supabase
          .from('client_streaks')
          .update({
            current_streak: newCurrentStreak,
            longest_streak: newLongestStreak,
            last_workout_date: today,
            total_workouts: newTotalWorkouts
          })
          .eq('client_id', clientId)
          .select()
          .single();

        if (error) throw error;
        currentStreak = updatedStreak;
      }

      setStreak(currentStreak);

      // Check for new achievements
      const earnedIds = new Set(earnedAchievements.map(ea => ea.achievement_id));

      for (const achievement of achievements) {
        if (earnedIds.has(achievement.id)) continue;

        let earned = false;

        if (achievement.requirement_type === 'total_workouts') {
          earned = currentStreak.total_workouts >= achievement.requirement_value;
        } else if (achievement.requirement_type === 'current_streak') {
          earned = currentStreak.current_streak >= achievement.requirement_value;
        }

        if (earned) {
          const { error } = await supabase
            .from('client_achievements')
            .insert({
              client_id: clientId,
              achievement_id: achievement.id
            });

          if (!error) {
            newUnlocked.push({ achievement, isNew: true });
            earnedIds.add(achievement.id);
          }
        }
      }

      if (newUnlocked.length > 0) {
        setNewlyUnlocked(newUnlocked);
        await loadData();
      }

      return newUnlocked;
    } catch (error) {
      console.error('Error recording workout:', error);
      return [];
    }
  }, [clientId, streak, achievements, earnedAchievements, loadData]);

  const clearNewlyUnlocked = useCallback(() => {
    setNewlyUnlocked([]);
  }, []);

  const getTotalPoints = useCallback(() => {
    return earnedAchievements.reduce((sum, ea) => {
      return sum + (ea.achievement?.points || 0);
    }, 0);
  }, [earnedAchievements]);

  return {
    streak,
    achievements,
    earnedAchievements,
    newlyUnlocked,
    loading,
    recordWorkout,
    clearNewlyUnlocked,
    getTotalPoints,
    reload: loadData
  };
}
